/**************************************
 * [COMPONENT]: L-HEADER-MOBILE-DRAWER
 **************************************/
if (!customElements.get('l-header-mobile-drawer')) {
	class LTAHeaderMobile extends HTMLElement {
		constructor() {
			super()
			this.animationDuration = 400
			this.header = this.closest('.l-header')
		}

		connectedCallback() {
			this._cacheElements()
			this._attachEvents()
			this._setupScrollAnimation()
		}

		disconnectedCallback() {
			this.removeEventListener('scroll', this._boundOnScroll)
			window.removeEventListener('scroll', this.onWindowScroll)
		}

		_cacheElements() {
			this.scrollAnimationEls = Array.from(this.querySelectorAll('.l-scroll-animate'))
			this.accordionTriggerEls = Array.from(this.querySelectorAll('.l-headerMobileDrawer__accordion-trigger'))
		}

		_attachEvents() {
			this.accordionTriggerEls.forEach((trigger) => {
				trigger.addEventListener('click', (e) => this._toggleAccordion(e))
			})
			window.addEventListener('scroll', this.onWindowScroll)
		}

		_toggleAccordion(event) {
			const trigger = event.currentTarget
			console.log(trigger);
			const panelId = trigger.getAttribute('aria-controls')
			const panel = this.querySelector(`#${panelId}`)
			if (!panel) return

			const isOpen = trigger.getAttribute('aria-expanded') === 'true'

			trigger.setAttribute('aria-expanded', !isOpen)
			panel.classList.toggle('is-open', !isOpen)
		}

		_setupScrollAnimation() {
			if (!this.scrollAnimationEls || this.scrollAnimationEls.length === 0) return
			this._boundOnScroll = this._onScrollAnimation.bind(this)
			this.addEventListener('scroll', this._boundOnScroll, { passive: true })
			this._onScrollAnimation()
		}

		_onScrollAnimation() {
			const currentScroll = this.scrollTop
			const scrollable = this.scrollHeight - this.clientHeight
			const total = this.scrollAnimationEls.length

			if (total === 0 || scrollable === 0) return

			const percent = Math.min(Math.max(currentScroll / scrollable, 0), 1) * 1.5
			const activeCount = Math.round(percent * total)
			this.scrollAnimationEls.forEach((el, idx) => {
				el.classList.toggle('is-active', idx < activeCount)
			})
		}

		onWindowScroll = () => {
			const currentWindowScroll = window.scrollY
			if (window.innerWidth < 990) {
				if (currentWindowScroll > 0 && !this.header?.classList.contains('is-sticky')) {
					this.style.setProperty('--translate-height', `calc(var(--l-header-full-height) - ${currentWindowScroll}px - var(--l-header-main-height) - 10px)`)
				} else {
					this.style.removeProperty('--translate-height')
				}
			}
		}
	}

	customElements.define('l-header-mobile-drawer', LTAHeaderMobile)
}

/**************************************
 * [COMPONENT]: L-HEADER
 **************************************/

if (!customElements.get('l-header')) {
	class LTAHeader extends HTMLElement {
		constructor() {
			super()
			this.menuListEls = null
			this.productsMenuEls = null
			this.productsMenuLinksEls = null
			this.clickOutsideHandler = null
		}

		connectedCallback() {
			// Elements
			this.headerWrapperEl = this.closest('.l-header')
			this.menuListEls = this.querySelectorAll('[data-lv1-link]')
			this.productsMenuEls = document.querySelectorAll('[data-header-product-menu]')
			this.productsMenuLinksEls = this.querySelectorAll('[data-products-menu-link]')

			// Events
			this.setupEventListeners()
		}

		/**
		 * Cleanup event listeners on disconnect
		 */
		disconnectedCallback() {
			if (this.clickOutsideHandler) {
				document.removeEventListener('click', this.clickOutsideHandler)
			}
		}

		/**
		 * Setup all event listeners
		 */
		setupEventListeners() {
			this.setupMenuListEvents()
			this.setupProductsMenuEvents()
			this.setupClickOutsideHandler()
			this.handleStickyHeader()
		}

		handleStickyHeader() {
			const setHeights = () => {
				if (!this.headerWrapperEl) return
				if (!this.headerWrapperEl.classList.contains('is-sticky')) {
					const full = this.headerWrapperEl.offsetHeight
					const main = this.offsetHeight
					document.documentElement.style.setProperty('--l-header-full-height', `${full}px`)
					document.documentElement.style.setProperty('--l-header-main-height', `${main}px`)
					document.documentElement.style.setProperty('--l-header-sticky-threshold', `${full - main}px`)
				}
			}

			const getStickyThreshold = () =>
				parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--l-header-sticky-threshold')) || 0

			const checkStuck = () => {
				const barTop = this.getBoundingClientRect().top
				const scrollY = window.scrollY
				const threshold = getStickyThreshold()
				const isStuck = barTop <= 0 && scrollY >= threshold
				this.headerWrapperEl.classList.toggle('is-sticky', isStuck)
				this.headerWrapperEl.classList.toggle('is-sticky-back', !isStuck)
				if (!isStuck) requestAnimationFrame(setHeights)
			}

			window.addEventListener('scroll', checkStuck, { passive: true })
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					setHeights()
					checkStuck()
				})
			})
			if (typeof ResizeObserver !== 'undefined' && this.headerWrapperEl) {
				const ro = new ResizeObserver(() => {
					if (!this.headerWrapperEl.classList.contains('is-sticky')) setHeights()
				})
				ro.observe(this.headerWrapperEl)
			}
		}

		/**
		 * Setup menu list toggle events
		 */
		setupMenuListEvents() {
			this.menuListEls.forEach((linkEl) => {
				const anchorLinkEl = linkEl.querySelector('[data-link]')
				anchorLinkEl.addEventListener('click', (event) => {
					event.preventDefault()

					const dropdownLinkEl = linkEl.parentElement.querySelector('[data-dropdown-menu]')
					linkEl.parentElement.classList.toggle('is-active')
					l_trapFocus({ container: dropdownLinkEl })
				})
			})
		}

		setupProductsMenuEvents() {
			this.productsMenuLinksEls.forEach((linkEl) => {
				const menuId = linkEl.dataset.productsMenuLink
				const menuEl = document.getElementById(menuId)

				if (!menuEl) return

				linkEl.addEventListener('click', () => this.openProductsMenu(linkEl))
				linkEl.addEventListener('mouseover', () => this.openProductsMenu(linkEl))
				linkEl.addEventListener('mousemove', () => this.openProductsMenu(linkEl))
				linkEl.addEventListener('mouseenter', () => this.openProductsMenu(linkEl))
				linkEl.addEventListener('mouseleave', (event) => this.handleTriggerMouseLeave(event, linkEl))

				menuEl.addEventListener('mouseleave', (event) => this.handleTriggerMouseLeave(event, linkEl))
			})
			
			this.setupGlobalMouseLeaveHandler()
		}
		
		setupGlobalMouseLeaveHandler() {
			document.addEventListener('mousemove', (event) => {
				const activeMenus = Array.from(this.productsMenuEls).filter(menu =>
					menu.classList.contains('is-active')
				)

				if (activeMenus.length === 0) {
					clearTimeout(this._globalCloseTimer)
					this._globalCloseTimer = null
					return
				}

				activeMenus.forEach(menuEl => {
					const triggerEl = Array.from(this.productsMenuLinksEls).find(
						link => link.dataset.productsMenuLink === menuEl.id
					)

					if (!triggerEl) return

					const triggerRect = triggerEl.getBoundingClientRect()
					const headerRect = this.getBoundingClientRect()
					const menuRect = menuEl.getBoundingClientRect()

					const isOutsideTrigger = (
						event.clientX < triggerRect.left ||
						event.clientX > triggerRect.right ||
						event.clientY < triggerRect.top ||
						event.clientY > headerRect.bottom
					)

					const isOutsideMenu = (
						event.clientX < menuRect.left ||
						event.clientX > menuRect.right ||
						event.clientY < menuRect.top ||
						event.clientY > menuRect.bottom
					)

					if (isOutsideTrigger && isOutsideMenu) {
						if (!this._globalCloseTimer) {
							this._globalCloseTimer = setTimeout(() => {
								this.closeProductsMenu(triggerEl)
								this._globalCloseTimer = null
							}, 100)
						}
					} else {
						clearTimeout(this._globalCloseTimer)
						this._globalCloseTimer = null
					}
				})
			})
		}

		handleTriggerMouseLeave(event, linkEl) {
			const relatedTarget = event.relatedTarget
			const menuId = linkEl.dataset.productsMenuLink
			const menuEl = document.getElementById(menuId)

			const isInsideMenu = menuEl && menuEl.contains(relatedTarget)
			const isInsideHeader = this.contains(relatedTarget)

			if (!linkEl.contains(relatedTarget) && !isInsideMenu && !isInsideHeader) {
				this.closeProductsMenu(linkEl)
			}
		}

		/**
		 * Open products menu with animation
		 */
		openProductsMenu(linkEl) {
			clearTimeout(this._globalCloseTimer)
			this._globalCloseTimer = null

			const menuId = linkEl.dataset.productsMenuLink
			const menuEl = document.getElementById(menuId)

			if (!menuEl) return

			this.productsMenuEls.forEach((otherMenuEl) => {
				if (otherMenuEl !== menuEl && otherMenuEl.classList.contains('is-active')) {
					otherMenuEl.classList.remove('is-active')
					otherMenuEl.classList.add('l-hidden')
				}
			})

			l_setupVariablesBeforeOpenModal()
			l_lockBodyScroll('l-headerProductsMenu')

			menuEl.classList.remove('l-hidden')
			requestAnimationFrame(() => {
				menuEl.classList.add('is-active')
			})
		}

		closeProductsMenu(linkEl) {
			const menuId = linkEl.dataset.productsMenuLink
			const menuEl = document.getElementById(menuId)

			if (!menuEl) return

			menuEl.classList.remove('is-active')
			
			setTimeout(() => {
				if (!menuEl.classList.contains('is-active')) {
					menuEl.classList.add('l-hidden')
					l_unLockBodyScroll('l-headerProductsMenu')
				}
			}, 400)
		}

		/**
		 * Setup click outside handler
		 */
		setupClickOutsideHandler() {
			this.clickOutsideHandler = this.handleClickOutside.bind(this)
			document.addEventListener('click', this.clickOutsideHandler)
			document.addEventListener('keyup', (event) => {
				if (event.key.toUpperCase() === 'ESCAPE') {
					this.handleCloseByEscape(event)
				}
			})
		}

		handleCloseByEscape(event) {
			// Close all menu lists
			this.menuListEls.forEach((linkEl) => {
				linkEl.classList.remove('is-active')
			})

			// Close all products menus
			this.productsMenuEls.forEach((menuEl) => {
				menuEl.classList.remove('is-active')
			})

			this.productsMenuEls.forEach((menuEl) => {
				this.closeProductsMenu(menuEl)
			})
		}

		/**
		 * Handle click outside events
		 */
		handleClickOutside(event) {
			this.handleMenuListClickOutside(event)
			this.handleProductsMenuClickOutside(event)
		}

		/**
		 * Handle menu list click outside
		 */
		handleMenuListClickOutside(event) {
			this.menuListEls.forEach((linkEl) => {
				if (!linkEl.contains(event.target)) {
					linkEl.classList.remove('is-active')

					const dropdownLinkEl = linkEl.parentElement.querySelector('[data-dropdown-menu]')
					l_removeTrapFocus(dropdownLinkEl)
				}
			})
		}

		/**
		 * Handle products menu click outside
		 */
		handleProductsMenuClickOutside(event) {
			this.productsMenuEls.forEach((menuEl) => {
				const triggerEl = document.getElementById('header')?.querySelector(`[data-products-menu-link="${menuEl.id}"]`)

				const shouldClose =
					menuEl.classList.contains('is-active') && !menuEl.contains(event.target) && !triggerEl?.contains(event.target)

				if (shouldClose) {
					this.closeProductsMenu(triggerEl)
				}
			})
		}
	}

	customElements.define('l-header', LTAHeader)
}

/**************************************
 * [COMPONENT]: L-HEADER-MENU-LOCALIZATION
 **************************************/
if (!customElements.get('l-header-menu-localization')) {
	class LTAHeaderMenuLocalization extends LTACollapsibleTab {
		constructor() {
			super()
			this.form = null
			this.searchInputEls = []
			this.drawerLayer = null
			this.isMobileDrawer = false
			this._handleCountryChange = this._handleCountryChange.bind(this)
			this._handleClickOutside = this._handleClickOutside.bind(this)
		}

		connectedCallback() {
			this.isMobileDrawer = !!this.closest('l-header-mobile-drawer')
		
			if (!this.isMobileDrawer) {
				super.connectedCallback()
				this._setupOnClickOutside()
			}
		
			this.form = this.querySelector('form')
		
			if (this.isMobileDrawer) {
				this.drawerLayer = this.querySelector('[data-drawer-layer]')
				const drawerCard = this.closest('l-header-mobile-drawer')
				drawerCard.appendChild(this.drawerLayer)
		
				this.drawerLayer.querySelector('[data-layer-close]').addEventListener('click', () => this.close())
				this.querySelector('[data-trigger]').addEventListener('click', (e) => {
					e.preventDefault()
					this.open()
				})
				this.drawerLayer.addEventListener('click', this._handleCountryChange)
			}
		
			this.addEventListener('click', this._handleCountryChange)
			this._setupSearch()
		}

		disconnectedCallback() {
			document.removeEventListener('click', this._handleClickOutside)
			if (this.drawerLayer) {
				this.drawerLayer.removeEventListener('click', this._handleCountryChange)
			}
			super.disconnectedCallback && super.disconnectedCallback()
		}

		open() {
			if (this.isMobileDrawer) {
					this.drawerLayer.classList.add('is-active')
			} else {
				super.open()
				l_lockBodyScroll('header-menu-localization')
				const searchInput = this.querySelector('[data-content] [data-search-input]')
				if (searchInput) searchInput.focus()
			}
		}
		
		close() {
			if (this.isMobileDrawer) {
				this.drawerLayer.classList.remove('is-active')
				l_removeTrapFocus(this.drawerLayer)
				l_trapFocus({ container: this.closest('l-header-mobile-drawer') })
			} else {
				super.close()
				l_unLockBodyScroll('header-menu-localization')
			}
		}	

		_setupOnClickOutside() {
			document.addEventListener('click', this._handleClickOutside)
		}

		_handleClickOutside(event) {
			if (!this.contains(event.target) && this.classList.contains('is-expanded')) {
				this.close()
			}
		}

		_setupSearch() {
			const panels = [
				this.querySelector('[data-content]'),
				this.drawerLayer,
			].filter(Boolean)
		
			panels.forEach((panel) => {
				const searchInput = panel.querySelector('[data-search-input]')
				const selections = panel.querySelector('[data-selections]')
				if (!searchInput || !selections) return
		
				searchInput.addEventListener('input', (event) => {
					const value = event.target.value
					const items = selections.querySelectorAll('[data-id]')
					const query = l_handleize(value)
		
					items.forEach((el) => {
						const text = l_handleize(el.dataset.text)
						const id = l_handleize(el.dataset.id)
						const visible = !value || text.includes(query) || id.includes(query)
						el.classList.toggle('l-hidden', !visible)
					})
				})
			})
		}	

		_handleCountryChange(event) {
			const countryEl = event.target.closest('[data-id]')
			if (!countryEl) return

			const inputEl = this.form.querySelector('input[name="country_code"], input[name="language_code"]')
			if (inputEl) {
				inputEl.value = countryEl.dataset.id
			}

			this.form.submit()
			countryEl.dispatchEvent(new Event('submit', { bubbles: true }))
		}
	}

	customElements.define('l-header-menu-localization', LTAHeaderMenuLocalization)
}
/**************************************
 * [COMPONENT]: L-MENU-PRODUCT-LINK
 ***************************************/

if (!customElements.get('l-menu-product-link')) {
	class LMenuProductLink extends HTMLElement {
		constructor() {
			super()
		}

		connectedCallback() {
			this._setDataAttributes()
			if (!this._setVisibilityFromSession()) {
				this._fetchAndSetProductAvailability()
			}
		}

		_setDataAttributes() {
			const { currentMarket: currentMarketID, productUrl: productURL, productId: productID } = this.dataset
			this.currentMarketID = currentMarketID
			this.productURL = productURL
			this.productID = productID
		}

		_setVisibilityFromSession() {
			const key = `product-available-${this.productID}`
			const cached = sessionStorage.getItem(key)
			if (!cached) return false

			try {
				const { marketID, isAvailable } = JSON.parse(cached)
				if (marketID === this.currentMarketID) {
					this._toggleVisibility(isAvailable)
					return true
				}
			} catch (e) {
				console.warn('Session data parsing failed', e)
			}
			return false
		}

		_fetchAndSetProductAvailability() {
			const url = `${this.productURL}?section_id=a-ajax-product-valid-check`

			fetch(url)
				.then((res) => res.text())
				.then((html) => {
					const productEl = l_getSectionDOM(html)
					const isAvailable = !!(productEl && productEl.querySelector('#valid-product-available'))

					this._toggleVisibility(isAvailable)

					const value = JSON.stringify({
						marketID: this.currentMarketID,
						isAvailable,
					})
					sessionStorage.setItem(`product-available-${this.productID}`, value)
				})
				.catch((e) => {
					console.warn('Could not fetch product availability.', e)
					this._toggleVisibility(false)
				})
		}

		_toggleVisibility(isAvailable) {
			this.classList.toggle('t4s-d-none', !isAvailable)
		}
	}

	customElements.define('l-menu-product-link', LMenuProductLink)
}

/**************************************
 * [SECTION]: L-STOCK-HELLO-BANNER
 ***************************************/

if (!customElements.get('l-stock-hello-banner')) {
	class LTAStockHelloBanner extends HTMLElement {
		constructor() {
			super()

			this.handleVisitedBefore = this.handleVisitedBefore.bind(this)
			this.handleCartUpdate = this.handleCartUpdate.bind(this)
		}

		connectedCallback() {
			// Set initial visibility based on window property, or listen for the event
			if (typeof window.isReturningCustomer === 'boolean') {
				this.updateReturningCustomer(window.isReturningCustomer)
			} else {
				document.addEventListener('l:hasVisitedBefore', this.handleVisitedBefore, { once: true })
			}

			document.addEventListener('cart:update:count', this.handleCartUpdate)
		}

		disconnectedCallback() {
			document.removeEventListener('l:hasVisitedBefore', this.handleVisitedBefore)
			document.removeEventListener('cart:update:count', this.handleCartUpdate)
		}

		handleVisitedBefore(event) {
			const { isReturningCustomer } = event.detail
			this.updateReturningCustomer(isReturningCustomer)
		}

		updateReturningCustomer(isReturningCustomer) {
			this.classList.toggle('t4s-d-none', !isReturningCustomer)
		}

		handleCartUpdate(event) {
			const cartCount = parseInt(event.detail.count, 10) || 0
			this.classList.toggle('is-active', cartCount > 0)
		}
	}

	customElements.define('l-stock-hello-banner', LTAStockHelloBanner)
}

/**************************************
 * [SECTION]: L-CALLOUT-BANNER-NEW-CUSTOMERS
 ***************************************/
if (!customElements.get('l-callout-banner-new-customers')) {
	class LTACalloutBannerNewCustomers extends HTMLElement {
		constructor() {
			super()
			this.handleVisitedBefore = this.handleVisitedBefore.bind(this)
			this.handleCartUpdate = this.handleCartUpdate.bind(this)
		}

		connectedCallback() {
			// Set initial banner visibility
			if (typeof window.isReturningCustomer === 'boolean') {
				this.updateBanner(window.isReturningCustomer)
			} else {
				document.addEventListener('l:hasVisitedBefore', this.handleVisitedBefore, { once: true })
			}

			document.addEventListener('cart:update:count', this.handleCartUpdate)
		}

		disconnectedCallback() {
			document.removeEventListener('l:hasVisitedBefore', this.handleVisitedBefore)
			document.removeEventListener('cart:update:count', this.handleCartUpdate)
		}

		handleVisitedBefore(event) {
			const { isReturningCustomer } = event.detail || {}
			this.updateBanner(isReturningCustomer)
		}

		updateBanner(isReturningCustomer) {
			this.classList.toggle('t4s-d-none', !!isReturningCustomer)
		}

		handleCartUpdate(event) {
			const cartCount = parseInt(event.detail?.count, 10) || 0
			this.classList.toggle('is-active', cartCount > 0)
		}
	}

	customElements.define('l-callout-banner-new-customers', LTACalloutBannerNewCustomers)
}

/**************************************
 * [COMPONENT]: L-MINI-CART-STOCK-MESSAGE
 ***************************************/

if (!customElements.get('l-mini-cart-stock-message')) {
	class LTAMiniCartStockMessage extends HTMLElement {
		constructor() {
			super()

			this.handleCartUpdated = this.handleCartUpdated.bind(this)
			this.handleVisitedBefore = this.handleVisitedBefore.bind(this)
		}

		connectedCallback() {
			document.addEventListener('l:cart:updated', this.handleCartUpdated)

			// if (window.isReturningCustomer) {
			// 	this.toggleElement()
			// } else {
			// 	document.addEventListener('l:hasVisitedBefore', this.handleVisitedBefore, { once: true })
			// }
		}

		disconnectedCallback() {
			document.removeEventListener('l:cart:updated', this.handleCartUpdated)
			// document.removeEventListener('l:hasVisitedBefore', this.handleVisitedBefore)
		}

		handleCartUpdated(event) {
			const product = event.detail

			console.clear()
			console.log('CART UPDATED')

			const isCustomizedProduct = product?.product_type === 'mws_apo_generated'
			this.toggleElement(isCustomizedProduct)
		}

		handleVisitedBefore() {
			this.toggleElement()
		}

		toggleElement(isCustomizedProduct) {
			let hasCustomizedProducts =
				typeof isCustomizedProduct !== 'undefined' ? isCustomizedProduct : this.dataset.hasCustomizedProducts === 'true'

			if (typeof isCustomizedProduct !== 'undefined') {
				this.dataset.hasCustomizedProducts = hasCustomizedProducts ? 'true' : 'false'
			}

			// const shouldHide = !window.isReturningCustomer || hasCustomizedProducts
			const shouldHide = hasCustomizedProducts

			this.classList.toggle('t4s-d-none', shouldHide)
		}
	}

	customElements.define('l-mini-cart-stock-message', LTAMiniCartStockMessage)
}

(() => {
	const setOffset = () => {
		const b = document.getElementById('FreeShippingBanner')
		document.documentElement.style.setProperty('--shipping-banner-offset', (b?.offsetHeight || 0) + 'px')
	}

	setOffset()
	document.addEventListener('DOMContentLoaded', setOffset)
	window.addEventListener('load', setOffset)
	;[500, 1500, 3000].forEach((t) => setTimeout(setOffset, t))
	window.addEventListener('resize', setOffset)
})()
